import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Coffee, Handshake, Sparkles } from 'lucide-react';

const Collaborate = () => {
  const [selectedServices, setSelectedServices] = useState([]);

  const services = [
    'Project & Program Management',
    'Organisational Transformation',
    'Strategy & Governance',
    'Social Impact & Value',
    'Change Management',
    'Climate Risk & Strategy',
    'Renewable Energy Delivery',
    'Other'
  ];

  const toggleService = (service) => {
    if (selectedServices.includes(service)) {
      setSelectedServices(selectedServices.filter(s => s !== service));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  return (
    <div data-testid="collaborate-page" className="bg-ozone-white min-h-screen">
      <Helmet>
        <title>Collaborate with Sustainify® | Contact Rachelle Cooper Kulkarni | Partnership Models</title>
        <meta name="description" content="Partner with Sustainify® — three collaboration models (Casual, Partner, Strategic). Contact Founder Rachelle Cooper Kulkarni for sustainability consulting on the Sunshine Coast and across Australia." />
        <link rel="canonical" href="https://www.sustainify.com.au/collaborate" />
      </Helmet>
      {/* Hero */}
      <div 
        className="relative bg-basalt-carbon text-ozone-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(26, 28, 30, 0.80), rgba(26, 28, 30, 0.80)), url(https://images.unsplash.com/photo-1600880292089-90a7e086ee0c)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="collaborate-title">
            Let's <span className="text-solar-kinetic">Sustainify</span><sup className="text-3xl">®</sup> Together
          </h1>
          <p className="text-xl text-ozone-white/80 max-w-3xl mx-auto mb-4">
            Your partners in impact, for a sustainable world.
          </p>
          <p className="text-lg text-ozone-white/70 max-w-2xl mx-auto">
            Start your impact journey today. We're ready to turn your vision into real-world outcomes.
          </p>
        </div>
      </div>

      {/* Service Selector */}
      <section className="py-16 bg-white" data-testid="service-selector-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-basalt-carbon mb-4 text-center">
            What services are you interested in?
          </h2>
          <p className="text-center text-basalt-carbon/70 mb-8">
            Select all that apply (optional - helps us understand your needs)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {services.map((service) => (
              <button
                key={service}
                onClick={() => toggleService(service)}
                data-testid={`service-${service.toLowerCase().replace(' ', '-')}`}
                className={`p-4 rounded-xl border-2 transition-all text-sm font-medium ${
                  selectedServices.includes(service)
                    ? 'border-kabi-teal bg-kabi-teal/10 text-kabi-teal'
                    : 'border-basalt-carbon/20 text-basalt-carbon hover:border-kabi-teal/50'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedServices.includes(service)
                      ? 'border-kabi-teal bg-kabi-teal'
                      : 'border-basalt-carbon/30'
                  }`}>
                    {selectedServices.includes(service) && (
                      <Check size={16} className="text-white" />
                    )}
                  </div>
                  <span className="text-center">{service}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Microsoft Form Embed */}
      <section className="py-16 bg-kabi-teal/5" data-testid="contact-form-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-basalt-carbon mb-4">
              Get in Touch
            </h2>
            <p className="text-basalt-carbon/70 max-w-2xl mx-auto">
              Fill out the form below and we'll get back to you within 24 hours to discuss how we can help.
            </p>
          </div>
          
          {/* Microsoft Forms iframe */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="microsoft-form-container">
            <iframe 
              src="https://forms.cloud.microsoft/r/msFPAZJhpU"
              width="100%"
              height="800px"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="Contact Form"
              className="w-full"
            >
              Loading...
            </iframe>
          </div>
        </div>
      </section>

      {/* Our Collaboration Models */}
      <section className="py-20 bg-ozone-white" data-testid="collaboration-models-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-4xl mx-auto">
            <span className="inline-block bg-kabi-teal/10 text-kabi-teal px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Our Collaboration Models
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-basalt-carbon mb-6 leading-tight">
              Delivering our sustainable future requires <span className="text-kabi-teal">collaboration, together.</span>
            </h2>
            <p className="text-lg text-basalt-carbon/80 leading-relaxed mb-3">
              Sustainify® partners with industry, community, and government to co-create pathways for an inclusive and thriving future.
            </p>
            <p className="text-lg text-basalt-carbon/80 leading-relaxed mb-6">
              If you're an organisation who shares our vision for transformative change, reach out and partner with us.
            </p>
            <p className="text-xl font-bold text-kabi-teal italic">
              Let's make impact together.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Casual Collaborator */}
            <div data-testid="collab-casual" className="bg-white rounded-2xl p-8 border-2 border-kabi-teal/20 hover:border-kabi-teal hover:shadow-xl transition-all flex flex-col">
              <div className="bg-solar-kinetic/20 text-kabi-teal w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Coffee size={32} />
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-solar-kinetic mb-2">Entry Level</div>
              <h3 className="text-2xl font-bold text-basalt-carbon mb-4 leading-tight">
                Casual Collaborator
              </h3>
              <p className="text-base text-basalt-carbon/75 leading-relaxed mb-4">
                Are you passionate about regenerative solutions and eager to connect with a like-minded organisation? Join Sustainify® as a Casual Collaborator.
              </p>
              <p className="text-sm font-semibold text-kabi-teal mb-3">This flexible opportunity is ideal for those who want to:</p>
              <ul className="space-y-2 text-sm text-basalt-carbon/80">
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Stay in the loop on sustainability and energy projects</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Share ideas and advice</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Make introductions or referrals</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Contribute occasionally or on a one-off basis, without formal commitment — test the waters</span>
                </li>
              </ul>
            </div>

            {/* Partner */}
            <div data-testid="collab-partner" className="bg-kabi-teal text-ozone-white rounded-2xl p-8 border-2 border-kabi-teal hover:shadow-2xl transition-all flex flex-col relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-solar-kinetic text-basalt-carbon text-xs font-bold px-3 py-1 rounded-bl-lg">
                MOST POPULAR
              </div>
              <div className="bg-solar-kinetic text-basalt-carbon w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Handshake size={32} />
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-solar-kinetic mb-2">Project-Based</div>
              <h3 className="text-2xl font-bold mb-4 leading-tight">
                Partner
              </h3>
              <p className="text-base text-ozone-white/90 leading-relaxed mb-4">
                Ready to co-create impact? Become a Sustainify® Partner and collaborate with us on specific projects, joint bids, or co-designed knowledge sharing.
              </p>
              <p className="text-sm font-semibold text-solar-kinetic mb-3">As a Partner, you will:</p>
              <ul className="space-y-2 text-sm text-ozone-white/85">
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Contribute your sector expertise or technical skills to real-world projects</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Work alongside us to deliver meaningful outcomes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Access shared learning and new business opportunities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Contribute to collective impact while retaining flexibility</span>
                </li>
              </ul>
            </div>

            {/* Strategic Partner */}
            <div data-testid="collab-strategic" className="bg-white rounded-2xl p-8 border-2 border-kabi-teal/20 hover:border-kabi-teal hover:shadow-xl transition-all flex flex-col">
              <div className="bg-solar-kinetic/20 text-kabi-teal w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles size={32} />
              </div>
              <div className="text-xs font-bold uppercase tracking-wider text-solar-kinetic mb-2">Long-Term</div>
              <h3 className="text-2xl font-bold text-basalt-carbon mb-4 leading-tight">
                Strategic Partner
              </h3>
              <p className="text-base text-basalt-carbon/75 leading-relaxed mb-4">
                Seeking a deeper, long-term relationship? Sustainify®'s Strategic Partners are organisations and individuals that are committed to systemic, lasting change.
              </p>
              <p className="text-sm font-semibold text-kabi-teal mb-3">Together, we will:</p>
              <ul className="space-y-2 text-sm text-basalt-carbon/80">
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Co-design and pilot innovative solutions and shared strategy</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Collaboratively advocate for policy and innovation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Invest in joint research and capacity building</span>
                </li>
                <li className="flex items-start">
                  <span className="text-solar-kinetic mr-2 flex-shrink-0">✓</span>
                  <span>Share resourcing and collaborate on major initiatives</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <a
              href="mailto:info@sustainify.com.au?subject=Partnership%20Enquiry&body=Hi%20Sustainify%20team%2C%0A%0AI%27m%20interested%20in%20exploring%20a%20partnership%20with%20Sustainify.%0A%0ACollaboration%20Level%3A%20(Casual%20%2F%20Partner%20%2F%20Strategic)%0AName%3A%0AOrganisation%3A%0ARole%3A%0AAreas%20of%20Interest%3A%0A%0AThanks%21"
              data-testid="partnership-cta"
              className="inline-flex items-center space-x-2 bg-kabi-teal text-ozone-white px-8 py-4 rounded-full font-semibold hover:bg-kabi-teal/90 transition-all text-lg"
            >
              <Handshake size={20} />
              <span>Explore a Partnership</span>
            </a>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-basalt-carbon text-ozone-white" data-testid="founder-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Meet Our <span className="text-solar-kinetic">Founder</span>
          </h2>
          <p className="text-xl mb-4">
            <strong>Rachelle Cooper Kulkarni</strong> — Founder & Principal Consultant
          </p>
          <p className="text-ozone-white/80 leading-relaxed max-w-3xl mx-auto">
            Driven by purpose and years of learning what does and does not work, Rachelle built Sustainify® to create a new model of consulting for good. 
            With a decade of strategy, governance and delivery experience across Australia and the globe — from Indigenous communities 
            to the World Energy Council — Sustainify® is where global insights meet real-world change.
          </p>
        </div>
      </section>

      {/* Contact Info - Now at Bottom */}
      <section className="py-16 bg-ozone-white border-t border-basalt-carbon/10" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <h3 className="font-bold text-basalt-carbon mb-2 text-lg">Email</h3>
              <a href="mailto:info@sustainify.com.au" className="text-kabi-teal hover:underline">
                info@sustainify.com.au
              </a>
            </div>
            <div>
              <h3 className="font-bold text-basalt-carbon mb-2 text-lg">Location</h3>
              <p className="text-basalt-carbon/70">Kabi Kabi Country</p>
              <p className="text-basalt-carbon/70">The Wharf Mooloolaba, Sunshine Coast</p>
            </div>
            <div>
              <h3 className="font-bold text-basalt-carbon mb-2 text-lg">Response Time</h3>
              <p className="text-basalt-carbon/70">We typically respond within 24 hours</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Collaborate;